import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bell,
  CalendarCheck2,
  ChevronRight,
  CircleAlert,
  Clock3,
  Home,
  ListChecks,
  Plus,
  RefreshCw,
  UserRound,
  UsersRound,
} from "lucide-react";
import {
  previewSummary,
  type CustomerRow,
  type GovernanceSummary,
} from "./CustomerCrmWorkbench";
import { apiFetch, type CurrentUser } from "./lib/api";
import "./sales-mobile-home.css";

type WeeklyReportStatus = "DRAFT" | "SUBMITTED" | "RETURNED" | "APPROVED" | "MISSING";

type PendingWeeklyReport = {
  status: WeeklyReportStatus;
  label: string;
  reportId: string | null;
  needsAttention: boolean;
};

type WeeklyReportListResponse = {
  pendingWeeklyReport: PendingWeeklyReport;
  currentWeeklyReport?: PendingWeeklyReport;
};

const CRM_HOME_HREF = "/mobile/work/customers";
const CRM_INTAKE_HREF = "/mobile/work/customers?view=intake";
const CRM_WEEKLY_HREF = "/mobile/work/customers?view=weekly";
const WEEKLY_REPORT_HREF = "/work-management/weekly-reports?from=sales-mobile";

function localDayKey(value: Date) {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function currentWeekLabel(now = new Date()) {
  const monday = new Date(now);
  const day = monday.getDay() || 7;
  monday.setDate(monday.getDate() - day + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const format = (date: Date) => `${date.getMonth() + 1}月${date.getDate()}日`;
  return `${format(monday)} - ${format(sunday)}`;
}

function statusMeta(status: WeeklyReportStatus) {
  switch (status) {
    case "SUBMITTED":
      return { label: "已提交", detail: "本周周报已经提交", tone: "good" };
    case "APPROVED":
      return { label: "已通过", detail: "本周周报已经确认", tone: "good" };
    case "RETURNED":
      return { label: "需修改", detail: "周报已退回，请按意见补充", tone: "urgent" };
    case "DRAFT":
      return { label: "草稿", detail: "已有草稿，记得本周提交", tone: "warning" };
    default:
      return { label: "待创建", detail: "本周周报尚未开始", tone: "warning" };
  }
}

function isDueToday(customer: CustomerRow) {
  if (!customer.nextContactAt) return false;
  const date = new Date(customer.nextContactAt);
  return !Number.isNaN(date.getTime()) && localDayKey(date) === localDayKey(new Date());
}

function taskPriority(customer: CustomerRow) {
  if (customer.intakeDeadlineStatus === "overdue") return 0;
  if (customer.intakeDeadlineStatus === "due_soon") return 1;
  if (isDueToday(customer)) return 2;
  if (!customer.intakeComplete) return 3;
  return 4;
}

function taskDeadline(customer: CustomerRow) {
  if (customer.intakeDeadlineStatus === "overdue") return "已超过24小时";
  if (customer.intakeDeadlineStatus === "due_soon") {
    return `还剩 ${customer.intakeHoursRemaining ?? 0} 小时`;
  }
  if (customer.nextContactAt) {
    const date = new Date(customer.nextContactAt);
    if (!Number.isNaN(date.getTime())) {
      if (isDueToday(customer)) {
        return `今天 ${date.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false })}`;
      }
      return date.toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" });
    }
  }
  return "待安排";
}

function taskAction(customer: CustomerRow) {
  if (customer.nextAction?.trim()) return customer.nextAction.trim();
  if (customer.intakeMissing?.length) return `补齐${customer.intakeMissing.slice(0, 2).join("、")}`;
  return "查看客户记录";
}

function customerTitle(customer: CustomerRow) {
  return customer.customerName || customer.name || "未命名客户";
}

function addPreviewParam(href: string, previewMode: boolean) {
  if (!previewMode) return href;
  const separator = href.includes("?") ? "&" : "?";
  return `${href}${separator}preview=sales`;
}

export function SalesMobileHome({ user }: { user: CurrentUser }) {
  const previewMode = import.meta.env.DEV && user.id.startsWith("preview-");
  const [summary, setSummary] = useState<GovernanceSummary | null>(null);
  const [weeklyReport, setWeeklyReport] = useState<PendingWeeklyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    if (previewMode) {
      setSummary(previewSummary);
      setWeeklyReport({
        status: "DRAFT",
        label: currentWeekLabel(),
        reportId: "preview-weekly-report",
        needsAttention: true,
      });
      setLoading(false);
      return;
    }

    const [summaryResult, weeklyResult] = await Promise.allSettled([
      apiFetch<GovernanceSummary>("/sales-governance/summary"),
      apiFetch<WeeklyReportListResponse>("/work-management/weekly-reports"),
    ]);

    if (summaryResult.status === "fulfilled") {
      setSummary(summaryResult.value);
    } else {
      setSummary(null);
      setError("客户任务暂时未能更新，请稍后重试。");
    }

    if (weeklyResult.status === "fulfilled") {
      setWeeklyReport(
        weeklyResult.value.currentWeeklyReport ??
          weeklyResult.value.pendingWeeklyReport ??
          null,
      );
    } else {
      setWeeklyReport(null);
    }
    setLoading(false);
  }, [previewMode]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const ownRows = useMemo(() => {
    const rows = summary?.rows ?? [];
    if (summary?.scope !== "team") return rows;
    return rows.filter((customer) => customer.owner?.id === user.id);
  }, [summary, user.id]);

  const tasks = useMemo(
    () =>
      ownRows
        .filter((customer) => taskPriority(customer) < 4)
        .sort((left, right) => taskPriority(left) - taskPriority(right))
        .slice(0, 4),
    [ownRows],
  );
  const todayFollowupCount = ownRows.filter(isDueToday).length;
  const overdueCount = ownRows.filter(
    (customer) => customer.intakeDeadlineStatus === "overdue",
  ).length;
  const missingCount = ownRows.filter(
    (customer) => (customer.intakeMissing?.length ?? 0) > 0,
  ).length;
  const weeklyMeta = statusMeta(weeklyReport?.status ?? "MISSING");
  const today = new Intl.DateTimeFormat("zh-CN", {
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(new Date());

  return (
    <main className="sales-home-page">
      <section className="sales-home-shell route-enter">
        <header className="sales-home-header">
          <div>
            <span>大爱归心 · 线下渠道销售</span>
            <strong>{today}</strong>
          </div>
          <a className="sales-home-alert" href={addPreviewParam("/notifications", previewMode)} aria-label="查看通知">
            <Bell size={20} />
            {tasks.length > 0 ? <em>{tasks.length}</em> : null}
          </a>
        </header>

        <section className="sales-home-intro">
          <div>
            <span>今日工作</span>
            <h1>{user.displayName || user.name}，先处理重点客户。</h1>
            <p>系统只提醒缺口，已完成的内容不用重复填写。</p>
          </div>
          <a className="sales-home-primary" href={addPreviewParam(CRM_INTAKE_HREF, previewMode)}>
            <Plus size={22} />
            新增客户
          </a>
        </section>

        <section className="sales-home-metrics" aria-label="客户任务概览">
          <a href={addPreviewParam(`${CRM_HOME_HREF}?view=followup`, previewMode)}>
            <Clock3 size={18} />
            <strong>{loading ? "–" : todayFollowupCount}</strong>
            <span>今日待跟进</span>
          </a>
          <a className={overdueCount > 0 ? "is-urgent" : ""} href={addPreviewParam(CRM_WEEKLY_HREF, previewMode)}>
            <CircleAlert size={18} />
            <strong>{loading ? "–" : overdueCount}</strong>
            <span>已超24小时</span>
          </a>
          <a href={addPreviewParam(CRM_WEEKLY_HREF, previewMode)}>
            <ListChecks size={18} />
            <strong>{loading ? "–" : missingCount}</strong>
            <span>待补资料</span>
          </a>
        </section>

        <a className={`sales-home-weekly is-${weeklyMeta.tone}`} href={addPreviewParam(WEEKLY_REPORT_HREF, previewMode)}>
          <span className="sales-home-weekly-icon"><CalendarCheck2 size={22} /></span>
          <span>
            <small>本周周报 · {weeklyReport?.label || currentWeekLabel()}</small>
            <strong>{weeklyMeta.detail}</strong>
          </span>
          <em>{weeklyMeta.label}</em>
          <ChevronRight size={18} />
        </a>

        <section className="sales-home-priority">
          <div className="sales-home-section-head">
            <div>
              <span>按时限排序</span>
              <h2>今天优先处理</h2>
            </div>
            <a href={addPreviewParam(CRM_HOME_HREF, previewMode)}>全部客户 <ChevronRight size={16} /></a>
          </div>

          {loading ? (
            <div className="sales-home-skeleton" aria-label="正在读取客户任务">
              <span />
              <span />
              <span />
            </div>
          ) : tasks.length > 0 ? (
            <div className="sales-home-task-list">
              {tasks.map((customer) => {
                const priority = taskPriority(customer);
                return (
                  <a
                    className={`sales-home-task priority-${priority}`}
                    href={addPreviewParam(`${CRM_HOME_HREF}?view=followup&customerId=${encodeURIComponent(customer.id)}`, previewMode)}
                    key={customer.id}
                  >
                    <span className="sales-home-task-mark" aria-hidden="true" />
                    <span className="sales-home-task-copy">
                      <strong>{customerTitle(customer)}</strong>
                      <small>{customer.contactName || customer.customerType || "客户联系人待补"}</small>
                      <b>{taskAction(customer)}</b>
                    </span>
                    <span className="sales-home-task-time">
                      <em>{taskDeadline(customer)}</em>
                      <ChevronRight size={18} />
                    </span>
                  </a>
                );
              })}
            </div>
          ) : (
            <div className="sales-home-empty">
              <CalendarCheck2 size={28} />
              <div>
                <strong>今天没有紧急客户任务</strong>
                <span>可继续新增客户，或查看全部客户记录。</span>
              </div>
            </div>
          )}

          {error ? (
            <div className="sales-home-error" role="status">
              <span>{error}</span>
              <button onClick={() => void loadData()} type="button">
                <RefreshCw size={15} /> 重新读取
              </button>
            </div>
          ) : null}
        </section>

        <p className="sales-home-rule-note">
          每次实质沟通后及时补齐客户、结论、下一步与沟通凭证；周报直接汇总本周记录。
        </p>

        <nav className="sales-home-bottom-nav" aria-label="销售工作导航">
          <a className="active" href={addPreviewParam("/mobile", previewMode)}>
            <Home size={22} />
            <span>今日</span>
          </a>
          <a href={addPreviewParam(CRM_HOME_HREF, previewMode)}>
            <UsersRound size={22} />
            <span>客户</span>
          </a>
          <a className="center" href={addPreviewParam(CRM_INTAKE_HREF, previewMode)}>
            <span><Plus size={30} /></span>
            <b>录客户</b>
          </a>
          <a href={addPreviewParam(WEEKLY_REPORT_HREF, previewMode)}>
            <CalendarCheck2 size={22} />
            <span>周报</span>
          </a>
          <a href={addPreviewParam("/mobile/me", previewMode)}>
            <UserRound size={22} />
            <span>我的</span>
          </a>
        </nav>
      </section>
    </main>
  );
}
